/**
 * Patients API - extends the generated OpenAPI client
 *
 * Provides cache management for patient clinical info (demographics, medications, diagnoses)
 * with automatic invalidation when patient data is updated.
 */

import { enhancedApi } from '@store/generated/patients';

/**
 * Feature-specific tag types for patient cache management
 */
const PATIENT_TAG_TYPES = [
  'PatientClinicalInfo',
  'PatientCommunicationsPreferences',
  'PatientContext',
  'PatientInfo',
  'PatientModuleData',
] as const;

/**
 * Patients API - extends the generated endpoints with cache tags
 */
export const patientsApi = enhancedApi.enhanceEndpoints({
  addTagTypes: PATIENT_TAG_TYPES,
  endpoints: {
    /**
     * GET /v2/provider/patients/{patientId}/clinical-info
     * Fetches patient demographics + medications + diagnoses + last session
     */
    getV2ProviderPatientsByPatientIdClinicalInfo: {
      providesTags: (_result, _error, { patientId }) => [
        { type: 'PatientClinicalInfo' as const, id: patientId },
      ],
    },

    /**
     * PATCH /v2/provider/patients/{patientId}
     * Updates patient demographics fields
     * Invalidates clinical info cache to trigger refetch
     */
    patchV2ProviderPatientsByPatientId: {
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: 'PatientClinicalInfo' as const, id: patientId },
        { type: 'PatientInfo' as const, id: patientId },
      ],
    },

    /**
     * GET /v2/provider/patients/{patientId}/communications-preferences
     * Fetches patient communication preferences including preferred language
     */
    getV2ProviderPatientsByPatientIdCommunicationsPreferences: {
      providesTags: (_result, _error, { patientId }) => [
        { type: 'PatientCommunicationsPreferences' as const, id: patientId },
      ],
    },

    /**
     * PUT /v2/provider/patients/{patientId}/communications-preferences/language
     * Updates patient preferred language (syncs to Stripe)
     */
    putV2ProviderPatientsByPatientIdCommunicationsPreferencesLanguage: {
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: 'PatientCommunicationsPreferences' as const, id: patientId },
      ],
    },

    /**
     * PUT /v2/provider/patients/{patientId}/communications-preferences/{preferenceKey}
     * Updates a specific communication preference (email, sms, phone_call)
     */
    putV2ProviderPatientsByPatientIdCommunicationsPreferencesAndPreferenceKey: {
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: 'PatientCommunicationsPreferences' as const, id: patientId },
      ],
    },

    /**
     * GET /v2/provider/patients/{patientId}/info
     * Fetches all patient data in a single nested response
     */
    getV2ProviderPatientsByPatientIdInfo: {
      providesTags: (_result, _error, { patientId }) => [
        { type: 'PatientInfo' as const, id: patientId },
      ],
    },

    /**
     * POST /v2/provider/patients/{patientId}/diagnoses
     * Adds a diagnosis to the patient
     *
     * Invalidates PatientModuleData/PatientContext so surfaces that read
     * diagnoses via the legacy v1 module-data endpoint (EncounterViewV2
     * diagnoses tab, patient context bar) refetch after a v2 write.
     */
    postV2ProviderPatientsByPatientIdDiagnoses: {
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: 'PatientInfo' as const, id: patientId },
        { type: 'PatientClinicalInfo' as const, id: patientId },
        { type: 'PatientModuleData' as const, id: patientId },
        { type: 'PatientContext' as const, id: patientId },
      ],
    },

    /**
     * PATCH /v2/provider/patients/{patientId}/diagnoses/{diagnosisId}
     * Updates a diagnosis on the patient
     */
    patchV2ProviderPatientsByPatientIdDiagnosesAndDiagnosisId: {
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: 'PatientInfo' as const, id: patientId },
        { type: 'PatientClinicalInfo' as const, id: patientId },
        { type: 'PatientModuleData' as const, id: patientId },
        { type: 'PatientContext' as const, id: patientId },
      ],
    },

    /**
     * DELETE /v2/provider/patients/{patientId}/diagnoses/{diagnosisId}
     * Removes a diagnosis from the patient
     */
    deleteV2ProviderPatientsByPatientIdDiagnosesAndDiagnosisId: {
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: 'PatientInfo' as const, id: patientId },
        { type: 'PatientClinicalInfo' as const, id: patientId },
        { type: 'PatientModuleData' as const, id: patientId },
        { type: 'PatientContext' as const, id: patientId },
      ],
    },

    /**
     * POST /v2/provider/patients/{patientId}/medications
     * Adds a medication to the patient
     *
     * Invalidates PatientModuleData/PatientContext so surfaces that read
     * medications via the legacy v1 module-data endpoint (EncounterViewV2
     * med box, patient context bar) refetch after a v2 write.
     */
    postV2ProviderPatientsByPatientIdMedications: {
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: 'PatientInfo' as const, id: patientId },
        { type: 'PatientClinicalInfo' as const, id: patientId },
        { type: 'PatientModuleData' as const, id: patientId },
        { type: 'PatientContext' as const, id: patientId },
      ],
    },

    /**
     * PATCH /v2/provider/patients/{patientId}/medications/{medicationId}
     * Updates a medication on the patient
     */
    patchV2ProviderPatientsByPatientIdMedicationsAndMedicationId: {
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: 'PatientInfo' as const, id: patientId },
        { type: 'PatientClinicalInfo' as const, id: patientId },
        { type: 'PatientModuleData' as const, id: patientId },
        { type: 'PatientContext' as const, id: patientId },
      ],
    },

    /**
     * DELETE /v2/provider/patients/{patientId}/medications/{medicationId}
     * Removes a medication from the patient
     */
    deleteV2ProviderPatientsByPatientIdMedicationsAndMedicationId: {
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: 'PatientInfo' as const, id: patientId },
        { type: 'PatientClinicalInfo' as const, id: patientId },
        { type: 'PatientModuleData' as const, id: patientId },
        { type: 'PatientContext' as const, id: patientId },
      ],
    },

    /**
     * POST /v2/provider/patients/{patientId}/allergies
     * Adds an allergy to the patient
     *
     * Invalidates PatientModuleData/PatientContext so surfaces that read
     * allergies via the legacy v1 module-data endpoint (patient context
     * bar popover) refetch after a v2 write.
     */
    postV2ProviderPatientsByPatientIdAllergies: {
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: 'PatientInfo' as const, id: patientId },
        { type: 'PatientClinicalInfo' as const, id: patientId },
        { type: 'PatientModuleData' as const, id: patientId },
        { type: 'PatientContext' as const, id: patientId },
      ],
    },

    /**
     * PATCH /v2/provider/patients/{patientId}/allergies/{allergyId}
     * Updates an allergy on the patient
     */
    patchV2ProviderPatientsByPatientIdAllergiesAndAllergyId: {
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: 'PatientInfo' as const, id: patientId },
        { type: 'PatientClinicalInfo' as const, id: patientId },
        { type: 'PatientModuleData' as const, id: patientId },
        { type: 'PatientContext' as const, id: patientId },
      ],
    },

    /**
     * DELETE /v2/provider/patients/{patientId}/allergies/{allergyId}
     * Removes an allergy from the patient
     */
    deleteV2ProviderPatientsByPatientIdAllergiesAndAllergyId: {
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: 'PatientInfo' as const, id: patientId },
        { type: 'PatientClinicalInfo' as const, id: patientId },
        { type: 'PatientModuleData' as const, id: patientId },
        { type: 'PatientContext' as const, id: patientId },
      ],
    },
  },
});

// =============================================================================
// User-Friendly Hook Exports
// =============================================================================

// Queries
export const usePatientClinicalInfoQuery =
  patientsApi.useGetV2ProviderPatientsByPatientIdClinicalInfoQuery;
export const useLazyPatientClinicalInfoQuery =
  patientsApi.useLazyGetV2ProviderPatientsByPatientIdClinicalInfoQuery;
export const useCommunicationsPreferencesQuery =
  patientsApi.useGetV2ProviderPatientsByPatientIdCommunicationsPreferencesQuery;
export const usePatientInfoQuery = patientsApi.useGetV2ProviderPatientsByPatientIdInfoQuery;

// Mutations — patient update
export const useUpdatePatientMutation = patientsApi.usePatchV2ProviderPatientsByPatientIdMutation;
export const useUpdateLanguagePreferenceMutation =
  patientsApi.usePutV2ProviderPatientsByPatientIdCommunicationsPreferencesLanguageMutation;
export const useUpdateCommunicationPreferenceMutation =
  patientsApi.usePutV2ProviderPatientsByPatientIdCommunicationsPreferencesAndPreferenceKeyMutation;

// Mutations — clinical data CRUD
export const useAddDiagnosisMutation =
  patientsApi.usePostV2ProviderPatientsByPatientIdDiagnosesMutation;
export const useRemoveDiagnosisMutation =
  patientsApi.useDeleteV2ProviderPatientsByPatientIdDiagnosesAndDiagnosisIdMutation;
export const useAddMedicationMutation =
  patientsApi.usePostV2ProviderPatientsByPatientIdMedicationsMutation;
export const useRemoveMedicationMutation =
  patientsApi.useDeleteV2ProviderPatientsByPatientIdMedicationsAndMedicationIdMutation;
export const useAddAllergyMutation =
  patientsApi.usePostV2ProviderPatientsByPatientIdAllergiesMutation;
export const useRemoveAllergyMutation =
  patientsApi.useDeleteV2ProviderPatientsByPatientIdAllergiesAndAllergyIdMutation;
export const useUpdateDiagnosisMutation =
  patientsApi.usePatchV2ProviderPatientsByPatientIdDiagnosesAndDiagnosisIdMutation;
export const useUpdateMedicationMutation =
  patientsApi.usePatchV2ProviderPatientsByPatientIdMedicationsAndMedicationIdMutation;
export const useUpdateAllergyMutation =
  patientsApi.usePatchV2ProviderPatientsByPatientIdAllergiesAndAllergyIdMutation;

// =============================================================================
// Type Re-exports for Convenience
// =============================================================================

export type {
  AddAllergyRequest,
  AddDiagnosisRequest,
  AddMedicationRequest,
  AddressesSection,
  AllergyInfo2,
  // Communication Preferences types
  CommunicationsPreferencesResponse,
  ConsentSection,
  ContactMethodPreference,
  ContactSection,
  DemographicsSection,
  DiagnosisInfo4,
  EmergencyContactSection,
  EmploymentSection,
  InsuranceSummary2,
  MedicationInfo3,
  PatientAddressSchema,
  PatientClinicalInfo2 as PatientClinicalInfo,
  PatientClinicalInfoResponse,
  // Patient Info types (full patient data)
  PatientInfoResponse,
  UpdateAllergyRequest,
  UpdateCommunicationPreferenceRequest,
  UpdateDiagnosisRequest,
  UpdateLanguagePreferenceRequest,
  UpdateMedicationRequest,
  UpdatePatientRequest,
} from '@store/generated/patients';

